/**
 * Zod schemas for the Refine admin Venues CRUD surface (API-1472..1475).
 *
 * Mounted under `/api/v1/admin/venues/*` in `services/backend-api`. These
 * schemas are kept in `@loci/validation` (not co-located with the route)
 * so the same validators can be reused by:
 *   - the OpenAPI contract test (request/response shape parity)
 *   - the Refine client (`@refinedev/react-hook-form` can build a Zod
 *     resolver from the same source of truth instead of redeclaring the
 *     form shape)
 *   - future internal scripts / migration jobs that need to validate a
 *     venue payload before persisting.
 *
 * The `VenueType` enum is the full 23-value Prisma enum — COR-8 added
 * `ARENA` so the historical 18 + ARENA all validate, and COR-128 added
 * `PLACE_OF_WORSHIP`, `EVENT_SPACE`, `LIBRARY` and `CINEMA`. The Refine UI labels
 * a 6-value subset (BAR/RESTAURANT/PARK/ARENA/GALLERY/OTHER per
 * `VENUE_TYPE_LABELS`); subset enforcement is a UI concern, not a
 * boundary constraint — admins may legitimately set any column value.
 */
import { z } from 'zod';
import { strictBooleanQuery, venueTypeSchema } from '../schemas';

/** WGS84 latitude — narrower than Zod's `z.number()` to catch typos early. */
const latSchema = z.number().min(-90).max(90);
/** WGS84 longitude. */
const lngSchema = z.number().min(-180).max(180);
/**
 * WEB-363 — optional single-image URL backed by `Location.imageUrl`.
 * `.trim()` mirrors the events schema so admins can't accidentally store
 * a leading-space URL, and `.url()` rejects anything the browser wouldn't
 * navigate to. 2048 chars matches the events cap.
 */
const venueImageUrlSchema = z.string().trim().url().max(2048);

/**
 * Body for `POST /api/v1/admin/venues` (API-1473).
 *
 * `lat`/`lng` are optional: when omitted, the handler will geocode the
 * `address` via geo-core. When both are supplied, the geocode step is
 * skipped (admin-supplied coords win). Mixed supply (only `lat` OR only
 * `lng`) is rejected — partial coords are almost always a mistake.
 *
 * `venueType` is optional. Omit it to store NULL (never classified). OTHER
 * is a real "considered, nothing fit" verdict (COR-91), not the insert default.
 */
export const adminCreateVenueSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    address: z.string().trim().min(1).max(500),
    city: z.string().trim().min(1).max(120).optional(),
    state: z.string().trim().min(1).max(60).optional(),
    postalCode: z.string().trim().min(1).max(20).optional(),
    lat: latSchema.optional(),
    lng: lngSchema.optional(),
    venueType: venueTypeSchema.optional(),
    imageUrl: venueImageUrlSchema.optional(),
  })
  .strict()
  .refine((data) => (data.lat === undefined) === (data.lng === undefined), {
    message: 'lat and lng must be supplied together (or both omitted to trigger geocoding)',
    path: ['lat'],
  });
export type AdminCreateVenueBody = z.infer<typeof adminCreateVenueSchema>;

/**
 * Body for `PATCH /api/v1/admin/venues/:id` (API-1474).
 *
 * Optimistic concurrency: `updatedAt` is REQUIRED — the handler compares
 * it against the DB row's `updatedAt` and 409s on mismatch. This is the
 * same pattern API-1470 uses for events PATCH.
 *
 * All other fields are optional. Re-geocoding is triggered iff `address`
 * changes AND `lat`/`lng` are not explicitly supplied in this PATCH.
 *
 * `.strict()` rejects unknown keys so the Refine client cannot
 * accidentally send a server-managed field (e.g. `createdAt`) and have
 * it silently swallowed.
 */
export const adminUpdateVenueSchema = z
  .object({
    updatedAt: z.string().datetime({ offset: true }),
    name: z.string().trim().min(1).max(200).optional(),
    address: z.string().trim().min(1).max(500).optional(),
    city: z.string().trim().min(1).max(120).optional(),
    state: z.string().trim().min(1).max(60).optional(),
    postalCode: z.string().trim().min(1).max(20).optional(),
    lat: latSchema.optional(),
    lng: lngSchema.optional(),
    venueType: venueTypeSchema.optional(),
    // `null` clears the column on the server; `undefined` leaves it
    // untouched (Prisma `update` skips `undefined` keys).
    imageUrl: venueImageUrlSchema.nullable().optional(),
  })
  .strict()
  .refine(
    (data) => {
      // Mirror the create-schema invariant: partial coords are almost
      // always a mistake. Either both lat+lng or neither — but allow
      // both to be undefined (the common case where the PATCH only
      // touches a non-coord field).
      const latProvided = data.lat !== undefined;
      const lngProvided = data.lng !== undefined;
      return latProvided === lngProvided;
    },
    {
      message: 'lat and lng must be supplied together',
      path: ['lat'],
    },
  );
export type AdminUpdateVenueBody = z.infer<typeof adminUpdateVenueSchema>;

/**
 * `venueFactProvenance.venueType.source` written when an admin sets a venue's
 * type (PIP-315).
 *
 * The OSM lane stamps `openstreetmap` under the same key, and that stamp is
 * what puts a row in the ODbL-derived set (WEB-873). An admin edit overwrites
 * it with this value so a type a human chose is never attributed to
 * OpenStreetMap, and the OSM lane's candidate query skips any row carrying it
 * so a correction is not reverted when the lane's stamp expires (PIP-303).
 *
 * Deliberately NOT a member of `VENUE_AMENITY_SOURCE_PROVIDERS`. That array is
 * the public amenity-card wire enum, and an admin never writes an amenity
 * fact. The public reader ignores the `venueType` key, so this value never
 * reaches a public response.
 *
 * On-disk vocabulary: renaming it is a data migration, not a rename.
 */
export const ADMIN_VENUE_TYPE_PROVENANCE_SOURCE = 'admin';

/**
 * Query schema for `GET /api/v1/admin/venues` (API-1472).
 *
 * `sort` is `<field>:<direction>` (e.g. `name:asc`, `createdAt:desc`)
 * — the Refine `useTable` hook emits sort in this shape. Restricting the
 * sort field set prevents arbitrary column sorts (e.g. an attacker could
 * try `sort=password:asc` to side-channel a leak from a join).
 *
 * `eventYield28d` (PIP-315) is the materialized upcoming-28-day event count
 * (API-1705). It is nullable, and the handler sorts NULL last in both
 * directions.
 */
export const ADMIN_VENUES_SORT_FIELDS = [
  'name',
  'createdAt',
  'updatedAt',
  'eventYield28d',
] as const;
export type AdminVenuesSortField = (typeof ADMIN_VENUES_SORT_FIELDS)[number];

// Built from the array so adding a sort field is one edit.
const ADMIN_VENUES_SORT_PATTERN = new RegExp(
  `^(${ADMIN_VENUES_SORT_FIELDS.join('|')}):(asc|desc)$`,
);

export const adminVenuesListQuerySchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
    // Case-sensitive: the handler splits this into a Prisma column name, so
    // `NAME:asc` must be rejected here, not passed through as an unknown field.
    sort: z.string().regex(ADMIN_VENUES_SORT_PATTERN).default('name:asc'),
    q: z.string().trim().min(1).max(200).optional(),
    venueType: venueTypeSchema.optional(),
    // Accept boolean or the literal strings 'true'/'false'. Anything else
    // (e.g. `?hasEvents=foo`) fails validation rather than silently
    // dropping to undefined — CodeRabbit caught the silent-drop case on
    // the API-1472 PR.
    hasEvents: z
      .union([z.boolean(), z.literal('true'), z.literal('false')])
      .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
      .optional(),
    // PIP-315 — "needs a type": `venueType IS NULL` (never classified,
    // COR-91) OR `venueType = 'OTHER'`. `false` is the same as omitting it.
    // It cannot be combined with `venueType` — see the refine below.
    needsType: strictBooleanQuery,
  })
  .strict()
  .refine((q) => !(q.needsType === true && q.venueType !== undefined), {
    message: 'needsType cannot be combined with venueType',
    path: ['needsType'],
  });
export type AdminVenuesListQuery = z.infer<typeof adminVenuesListQuerySchema>;

/**
 * Body for `DELETE /api/v1/admin/venues/:id?force=true` (API-1475).
 *
 * When `?force=true`, the venue's linked active events are also
 * soft-deleted (set to CANCELLED status). `reason` is REQUIRED and is
 * recorded on every cascaded `EVENT_DELETED` audit (with prefix
 * `VENUE_DELETED:<venueId>`) so deletes are forensically traceable.
 *
 * When `?force` is absent or `false`, the body is ignored (handler
 * returns 409 if events are linked). Empty body is fine in that case.
 */
export const adminDeleteVenueForceBodySchema = z
  .object({
    reason: z.string().trim().min(1).max(500),
  })
  .strict();
export type AdminDeleteVenueForceBody = z.infer<typeof adminDeleteVenueForceBodySchema>;

/** Path param guard — same shape as the existing `adminIdParamSchema`. */
export const adminVenueIdParamSchema = z.string().trim().min(1).max(128);
